// src/components/ml/HealthDriftPanel.js
// AI Intelligence Center → Health & Drift: ML engine reachability, circuit breaker, caches,
// what version the engine actually serves vs. the registry, drift baselines, cache actions.
import React, { useState } from 'react';
import {
  Alert, AlertDescription, AlertIcon, Badge, Box, Button, Flex, Heading, HStack, Icon, SimpleGrid, Skeleton, Table, Tbody,
  Td, Text, Th, Thead, Tooltip, Tr, VStack, useColorModeValue, useToast,
} from '@chakra-ui/react';
import { MdBolt, MdCached, MdCheckCircle, MdCloudOff, MdModelTraining } from 'react-icons/md';
import mlOpsService from '../../services/mlOpsService';
import { ExplainButton } from './FlowSenseAIExplainer';
import { fmtDate, timeAgo } from './mlFormat';

const KV = ({ data }) => {
  const muted = useColorModeValue('gray.500', 'gray.400');
  const entries = Object.entries(data || {}).filter(([, v]) => v === null || ['number', 'string', 'boolean'].includes(typeof v));
  if (!entries.length) return <Text fontSize="sm" color={muted}>No data reported.</Text>;
  return (
    <SimpleGrid columns={2} spacingX={4} spacingY={1}>
      {entries.map(([k, v]) => (
        <React.Fragment key={k}>
          <Text fontSize="xs" color={muted}>{k.replace(/_/g, ' ')}</Text>
          <Text fontSize="xs" fontWeight="600" textAlign="right">{typeof v === 'number' ? (Math.abs(v) < 1 && v !== 0 ? v.toFixed(3) : v.toLocaleString()) : String(v)}</Text>
        </React.Fragment>
      ))}
    </SimpleGrid>
  );
};

export default function HealthDriftPanel({ overview: ov, loading, canManage, onTrain, onChanged, trainingKey }) {
  const toast = useToast();
  const [busy, setBusy] = useState(null);
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const muted = useColorModeValue('gray.500', 'gray.400');

  const reset = async (modelType, warm) => {
    setBusy(`${modelType || 'all'}:${warm ? 'warm' : 'reset'}`);
    try {
      const r = await mlOpsService.resetCache({ modelType, warm });
      toast({ title: warm ? (r.warmed ? 'Cache flushed and models reloaded' : 'Cache flushed (warm-up did not complete)') : 'ML engine cache flushed', status: 'success', duration: 3000 });
      onChanged?.();
    } catch (e) {
      toast({ title: 'Cache action failed', description: e.message, status: 'error', duration: 5000 });
    } finally { setBusy(null); }
  };

  if (!ov && loading) return <Skeleton h="400px" borderRadius="xl" />;
  if (!ov) return null;
  const e = ov.mlEngine || {};
  const circuit = e.circuit && typeof e.circuit === 'object' ? e.circuit : null;

  return (
    <VStack align="stretch" spacing={4}>
      <Flex bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4} gap={4} align="center" wrap="wrap">
        <Icon as={e.online ? MdCheckCircle : MdCloudOff} boxSize={8} color={e.online ? 'green.400' : 'red.400'} />
        <Box flex="1" minW="240px">
          <Heading size="sm">ML engine {e.online ? 'online' : 'offline'}</Heading>
          <Text fontSize="xs" color={muted}>{e.url}{e.timestamp ? ` · reported ${fmtDate(e.timestamp)}` : ''}{e.error ? ` · ${e.error}` : ''}</Text>
          {circuit && <Text fontSize="xs" color={muted}>Circuit breaker: {circuit.state || circuit.status || JSON.stringify(circuit)}</Text>}
        </Box>
        <ExplainButton topic="circuit_breaker" label="What is the circuit breaker?" />
        {canManage && (
          <HStack>
            <Button size="sm" leftIcon={<Icon as={MdCached} />} onClick={() => reset(null, false)} isLoading={busy === 'all:reset'} isDisabled={!e.online}>Flush all caches</Button>
            <Button size="sm" colorScheme="purple" leftIcon={<Icon as={MdBolt} />} onClick={() => reset(null, true)} isLoading={busy === 'all:warm'} isDisabled={!e.online}>Flush & warm up</Button>
          </HStack>
        )}
      </Flex>

      {!e.online && (
        <Alert status="warning" borderRadius="lg">
          <AlertIcon />
          <AlertDescription fontSize="sm">While the ML engine is offline FlowSense keeps working on fallbacks: weighted-score dispatch, keyword part matching and the Node SLA mirror. Training runs fail fast and autopilot waits.</AlertDescription>
        </Alert>
      )}

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4}>
          <Heading size="xs" mb={3} textTransform="uppercase" color={muted}>Model cache (loaded models)</Heading>
          <KV data={e.modelCache} />
        </Box>
        <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4}>
          <Heading size="xs" mb={3} textTransform="uppercase" color={muted}>Prediction cache</Heading>
          <KV data={e.predictionCache} />
        </Box>
      </SimpleGrid>

      <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4} overflowX="auto">
        <Flex mb={3} align="center"><Heading size="sm" flex="1">What the engine serves</Heading><ExplainButton topic="drift" label="What is drift?" /></Flex>
        <Table size="sm">
          <Thead><Tr><Th>Model</Th><Th>Registry production</Th><Th>Engine serving</Th><Th>Sync</Th><Th>Drift baseline</Th><Th>Runtime</Th><Th /></Tr></Thead>
          <Tbody>
            {ov.models.map(m => (
              <Tr key={m.key}>
                <Td fontWeight="600" fontSize="sm">{m.name}</Td>
                <Td fontFamily="mono" fontSize="xs">{m.champion ? m.champion.model_version : '—'}</Td>
                <Td fontFamily="mono" fontSize="xs">{m.engine?.activeVersion || '—'}</Td>
                <Td>
                  {m.engine?.inSync === true && <Badge colorScheme="green">in sync</Badge>}
                  {m.engine?.inSync === false && <Tooltip label="Flush this model's cache so the engine loads the registry's production version" hasArrow><Badge colorScheme="red">out of sync</Badge></Tooltip>}
                  {m.engine?.inSync == null && <Badge>n/a</Badge>}
                </Td>
                <Td fontSize="xs">{m.engine?.baselineStored ? `${m.engine.baselineFeatures} features` : 'none'}</Td>
                <Td fontSize="xs" maxW="220px">{m.engine?.runtime ? Object.entries(m.engine.runtime).filter(([, v]) => typeof v === 'number').slice(0, 3).map(([k, v]) => `${k.replace(/_/g, ' ')} ${Math.abs(v) < 1 && v !== 0 ? v.toFixed(2) : Math.round(v)}`).join(' · ') : '—'}</Td>
                <Td>
                  {canManage && (
                    <HStack spacing={1}>
                      <Tooltip label="Flush this model's cache" hasArrow><Button size="xs" variant="ghost" onClick={() => reset(m.key, false)} isLoading={busy === `${m.key}:reset`} isDisabled={!e.online}><Icon as={MdCached} /></Button></Tooltip>
                      <Tooltip label="Retrain now (e.g. after drift)" hasArrow><Button size="xs" variant="ghost" colorScheme="purple" onClick={() => onTrain(m.key)} isLoading={trainingKey === m.key} isDisabled={Boolean(m.activeJob)}><Icon as={MdModelTraining} /></Button></Tooltip>
                    </HStack>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        <Text fontSize="xs" color={muted} mt={3}>Overview generated {timeAgo(ov.generatedAt)} · registry table <code>{ov.registryTable}</code></Text>
      </Box>
    </VStack>
  );
}
